import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { GlobalActionsResponse } from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { queryKeys } from "./queryKeys.ts";

const ONE_HOUR = 60 * 60 * 1000;

const useActions = () => {
  const { getToken, isAuthenticated } = useAuthentication();

  return useQuery({
    queryKey: queryKeys.actions(),
    queryFn: async (): Promise<GlobalActionsResponse> => {
      const token = await getToken();
      const response = await fetch(`/api/actions`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Actions could not be fetched");
      }
      return (await response.json()) as GlobalActionsResponse;
    },
    enabled: isAuthenticated,
    placeholderData: { actions: [], projects: {} },
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
};

export const useUserHasActionInProjects = () => {
  const { data: actions } = useActions();
  return useMemo(() => {
    const hasGlobalAction = (actionName: string) =>
      actions?.actions.includes(actionName) ?? false;
    const byProject = new Map(
      Object.entries(actions?.projects ?? {}).map(
        ([projectId, actionsInProject]) => [
          projectId,
          (actionName: string) =>
            hasGlobalAction(actionName) ||
            actionsInProject.includes(actionName),
        ],
      ),
    );
    // Projects without a role of the user only grant the global actions.
    return (projectId: string) => byProject.get(projectId) ?? hasGlobalAction;
  }, [actions]);
};

export const useUserHasAction = (projectId?: string) => {
  const { data: actions } = useActions();
  return useCallback(
    (actionName: string) =>
      actions?.actions.includes(actionName) ||
      (projectId != null &&
        (actions?.projects[projectId]?.includes(actionName) ?? false)),
    [actions, projectId],
  );
};
